import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import * as S from "./Toast.styled";

const ICONS = {
  success: "✓",
  error: "✕",
  warning: "!",
  info: "i",
};

// Only urgent toasts interrupt a screen reader; success/info are announced politely.
const ASSERTIVE_TYPES = new Set(["error", "warning"]);

const ToastItem = ({ toast }) => {
  const { dismiss } = useToast();
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const duration = toast.type === "error" ? 6000 : 4000;
  const assertive = ASSERTIVE_TYPES.has(toast.type);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: reduce ? 0 : 80, scale: reduce ? 1 : 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{
        opacity: 0,
        x: reduce ? 0 : 80,
        scale: reduce ? 1 : 0.9,
        transition: { duration: 0.15 },
      }}
      transition={
        reduce
          ? { duration: 0.15 }
          : { type: "spring", stiffness: 400, damping: 30 }
      }
    >
      <S.ToastRoot
        $type={toast.type}
        role={assertive ? "alert" : "status"}
        aria-live={assertive ? "assertive" : "polite"}
      >
        <S.IconWrap $type={toast.type}>{ICONS[toast.type]}</S.IconWrap>
        <S.Body>
          <S.Message>{toast.message}</S.Message>
          {toast.action && (
            <S.ActionBtn
              type="button"
              onClick={() => {
                toast.action.onClick();
                dismiss(toast.id);
              }}
            >
              {toast.action.label}
            </S.ActionBtn>
          )}
        </S.Body>
        <S.DismissBtn
          type="button"
          onClick={() => dismiss(toast.id)}
          aria-label={t("toast.dismiss", {
            defaultValue: "Dismiss notification",
          })}
        >
          ✕
        </S.DismissBtn>
        <S.ProgressBar $duration={duration} />
      </S.ToastRoot>
    </motion.div>
  );
};

const Toast = () => {
  const { toasts } = useToast();
  const { t } = useTranslation();

  return (
    <S.Viewport
      role="region"
      aria-label={t("toast.region_label", { defaultValue: "Notifications" })}
    >
      <AnimatePresence initial={false} mode="popLayout">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} />
        ))}
      </AnimatePresence>
    </S.Viewport>
  );
};

export default Toast;
